/**
 * Where a DOM media URL may point.
 *
 * A `<img>`/`<audio>`/`<video>` src is resolved by the BROWSER — it never goes
 * through `routedFetch` and cannot travel the WebRTC channel. The only thing this
 * module can decide is WHICH ORIGIN the browser will ask, and there is one origin
 * that is never allowed: the cloud app's own (`torik.ai` / `app.torik.ai` / the
 * configured cloud base).
 *
 * Why: hub media paths — `/s3/…` presigned URLs, `/uploads/…`, and the stable
 * `/api/images/<id>/content`, `/api/media-library/<id>/…` routes — are served by a
 * HUB (its nginx `/s3/` proxy or the hub app itself). The cloud origin answers them
 * with the cloud app's HTML 404: in production that measured as 13 x
 * `GET /s3/images/room/<id>/cover/<id>.jpg?X-Amz-…` plus 1 x
 * `GET /api/images/<id>/content` in a 48 h window, referer `https://app.torik.ai/`
 * (card t_c51a774b's sibling t_1574d279).
 *
 * So, when no HUB origin is reachable from this device, a hub media path resolves
 * to a PLACEHOLDER — a 1x1 transparent data URI, unique per source URL, so a
 * caller that maps render-src → stored-src (TiptapEditor) keeps them apart and the
 * saved HTML still holds the stable hub path. The bytes themselves arrive through
 * `imageAssetsStore` (blob URL over the app's API layer): that is the happy path,
 * and every surface that has an image id or S3 key already prefers it. The
 * placeholder is what such a surface falls back to when the channel cannot deliver
 * the bytes either.
 */
import {
  getDirectHubOrigin,
  getRuntimeConfig,
  isCapacitor,
  isElectron,
  isElectronLocalApiUrl,
  isPrivateOrLocalHostname,
  type RuntimeConfig,
} from '../config/runtime';

export interface MediaUrlOptions {
  transcode?: boolean;
}

const ACTIVE_HUB_IP_KEY = 'otto_active_hub_ip';
const ACTIVE_HUB_PORT_KEY = 'otto_active_hub_port';
const ACTIVE_HUB_SECURE_KEY = 'otto_active_hub_secure';
const ELECTRON_LOCAL_PROTOCOL = 'otto-local:';

/** 1x1 transparent GIF — paints nothing, requests nothing, shows no broken icon. */
const TRANSPARENT_PIXEL_GIF = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/** Hub media paths: only a hub (its nginx proxy or the hub app) answers these. */
export function isHubServedMediaPath(pathname: string): boolean {
  return (
    pathname.startsWith('/s3/') ||
    pathname.startsWith('/uploads/') ||
    pathname.startsWith('/api/images/') ||
    pathname.startsWith('/api/media-library/')
  );
}

/**
 * The placeholder for one un-fetchable hub URL.
 *
 * The `#` fragment is inert for the image decoder but keeps the result UNIQUE per
 * source URL: the note editor maps render-src → stored-src, so a single shared
 * placeholder would collapse several images onto one entry and rewrite them all
 * to the same stored path on save. The id is a hash of the source — exact
 * uniqueness for the handful of images a screen holds, nothing of the URL itself
 * in the DOM (a presigned URL carries its signature in the query string).
 */
const PLACEHOLDER_CACHE = new Map<string, string>();
const PLACEHOLDER_IDS = new Set<string>();

function hash32(value: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(36);
}

export function mediaPlaceholder(source: string): string {
  const cached = PLACEHOLDER_CACHE.get(source);
  if (cached) return cached;

  const base = hash32(source);
  let id = base;
  for (let n = 2; PLACEHOLDER_IDS.has(id); n += 1) id = `${base}-${n}`;
  PLACEHOLDER_IDS.add(id);

  const placeholder = `${TRANSPARENT_PIXEL_GIF}#media-unavailable-${id}`;
  PLACEHOLDER_CACHE.set(source, placeholder);
  return placeholder;
}

export function isMediaPlaceholder(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.startsWith(TRANSPARENT_PIXEL_GIF);
}

function stripApiPath(value: string): string {
  return value.replace(/\/api\/?$/, '').replace(/\/$/, '');
}

function isUsableHttpOrigin(origin: string | null | undefined): origin is string {
  if (!origin) return false;
  const trimmed = origin.trim();
  if (!trimmed) return false;
  if (trimmed === 'null') return false;
  if (trimmed.startsWith(ELECTRON_LOCAL_PROTOCOL)) return false;
  if (!/^https?:\/\//i.test(trimmed)) return false;
  try {
    const parsed = new URL(trimmed);
    return Boolean(parsed.hostname);
  } catch {
    return false;
  }
}

/** The `scheme://host:port` origin of an absolute http(s) URL, else null. */
export function httpOriginOf(url: string | null | undefined): string | null {
  if (!url) return null;
  if (!/^https?:\/\//i.test(url)) return null;
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

/**
 * Everything the DOM-media-origin decision needs, pre-resolved so the decision
 * itself is pure (and unit-tested — see `mediaLibraryUrls.test.ts`).
 */
export interface MediaOriginCandidates {
  /** `window.location.origin` when it is real http(s). */
  pageOrigin: string | null;
  /** `otto_active_hub_ip`/`_port` — the hub this device last connected to. */
  storedHubOrigin: string | null;
  /** `getDirectHubOrigin(config.apiUrl)` — the API base as a hub origin. */
  directHubOrigin: string | null;
  /** `config.apiUrl`: relative ('/api') on the hub's own web, else an absolute base. */
  apiUrl: string;
  /** `config.cloudApiUrl` — the cloud app's API base (https://torik.ai/api). */
  cloudApiUrl: string;
  /** `config.mode`. */
  mode: RuntimeConfig['mode'];
  /** Electron / Capacitor / a cloud session: the page is never served by the hub. */
  nativeShell: boolean;
}

/**
 * True when the page IS the cloud app: the deployed app at `app.torik.ai`, the
 * SPA embedded in otto-web, or any other non-private origin a cloud session runs
 * on. Its own origin cannot answer a hub path, so it is never a media origin
 * either — and an absolute URL pointing at it is a request not worth making.
 * (`mode === 'local'` means the page is behind the hub's own proxy: not this.)
 */
export function pageIsCloudApp(pageOrigin: string | null, mode: RuntimeConfig['mode']): boolean {
  if (!pageOrigin || mode === 'local') return false;
  try {
    return !isPrivateOrLocalHostname(new URL(pageOrigin).hostname);
  } catch {
    return false;
  }
}

/**
 * Origins that can only ever be the CLOUD app. A hub path resolved against one of
 * these is a request the cloud cannot answer, so they are never DOM media origins
 * and an absolute URL pointing at one is replaced by the placeholder.
 */
export function cloudAppOrigins(candidates: MediaOriginCandidates): string[] {
  const origins: string[] = [];
  const cloudBase = httpOriginOf(candidates.cloudApiUrl);
  if (cloudBase) origins.push(cloudBase);
  if (pageIsCloudApp(candidates.pageOrigin, candidates.mode) && candidates.pageOrigin) {
    origins.push(candidates.pageOrigin);
  }
  return origins;
}

/**
 * The hub origin DOM media may be served from, or null when this device has no
 * reachable hub (a cloud-only session: the browser could only ask the cloud app).
 */
export function chooseDomMediaOrigin(candidates: MediaOriginCandidates): string | null {
  const blocked = new Set(cloudAppOrigins(candidates));

  // 1. A page served BY the hub (the hub's own web UI behind its nginx proxy):
  //    /api, /uploads and /s3 all resolve against the page origin. The stored raw
  //    hub origin (e.g. http://wasp.local:16161) is not necessarily the name the
  //    browser can reach, so the page wins here.
  if (!candidates.nativeShell && candidates.apiUrl.startsWith('/')) {
    if (isUsableHttpOrigin(candidates.pageOrigin) && !blocked.has(candidates.pageOrigin)) {
      return candidates.pageOrigin;
    }
  }

  // 2. The hub this device last connected to over the LAN — the machine that
  //    serves the hub, reachable from a browser on the same network.
  if (isUsableHttpOrigin(candidates.storedHubOrigin) && !blocked.has(candidates.storedHubOrigin)) {
    return candidates.storedHubOrigin;
  }

  // 3. The configured API base when it points at a hub directly: Electron's
  //    `otto-local://` proxy, or a LAN hub address in the mobile app.
  if (isUsableHttpOrigin(candidates.directHubOrigin) && !blocked.has(candidates.directHubOrigin)) {
    return candidates.directHubOrigin;
  }

  // 4. No hub origin. Everything below this line is the cloud app or the page the
  //    cloud app is served from — never a hub.
  return null;
}

function isCloudAppUrl(url: URL, candidates: MediaOriginCandidates): boolean {
  return cloudAppOrigins(candidates).includes(url.origin);
}

/**
 * Resolve one media URL for DOM use, never against the cloud origin.
 *
 * - Relative hub paths resolve against the hub origin when there is one; without
 *   one they become the placeholder (the old behaviour asked the cloud app, which
 *   answers hub paths with its HTML 404).
 * - Absolute `/s3/…` URLs are rewritten onto the hub origin (the presigned
 *   signature pins the MinIO Host header, so the browser-facing host is
 *   irrelevant). Without a hub origin, a URL that points at the cloud app — a
 *   leftover rewrite from a cached payload — becomes the placeholder, while a URL
 *   pointing somewhere else (a LAN hub, a public MinIO / storage host) is kept as
 *   it is: the browser then asks THAT host, not the cloud.
 * - `data:`/`blob:` URLs pass through untouched.
 */
export function resolveMediaUrl(url: string | null | undefined, candidates: MediaOriginCandidates): string | null {
  if (!url) return null;
  if (/^(data:|blob:)/i.test(url)) return url;

  const origin = chooseDomMediaOrigin(candidates);

  if (/^https?:\/\//i.test(url)) {
    try {
      const parsed = new URL(url);
      if (parsed.pathname.startsWith('/s3/')) {
        if (origin) return origin + parsed.pathname + parsed.search;
        return isCloudAppUrl(parsed, candidates) ? mediaPlaceholder(url) : url;
      }
      if (!origin && isHubServedMediaPath(parsed.pathname) && isCloudAppUrl(parsed, candidates)) {
        return mediaPlaceholder(url);
      }
    } catch {
      // Keep the original URL on any parse failure
    }
    return url;
  }

  if (origin) return origin + (url.startsWith('/') ? url : `/${url}`);

  // Relative and no hub in reach. A hub media path would be asked of the cloud
  // app — the leak this module exists to prevent; anything else keeps the old
  // page-relative behaviour.
  return isHubServedMediaPath(url.startsWith('/') ? url : `/${url}`) ? mediaPlaceholder(url) : url;
}

function getStoredHubOrigin(): string | null {
  try {
    const ip = localStorage.getItem(ACTIVE_HUB_IP_KEY);
    const portRaw = localStorage.getItem(ACTIVE_HUB_PORT_KEY);
    const port = portRaw ? Number(portRaw) : NaN;
    if (!ip || !Number.isFinite(port) || port <= 0) return null;
    const secure = localStorage.getItem(ACTIVE_HUB_SECURE_KEY) === 'true';
    return `${secure ? 'https' : 'http'}://${ip}:${port}`;
  } catch {
    return null;
  }
}

function getPageHttpOrigin(): string | null {
  try {
    const { protocol, origin } = window.location;
    if (protocol === 'http:' || protocol === 'https:') {
      if (origin && origin !== 'null') return origin;
    }
  } catch {
    // ignore
  }
  return null;
}

/** The current runtime as the pure decision wants it. */
export function currentMediaOriginCandidates(): MediaOriginCandidates {
  const config = getRuntimeConfig();
  let directHubOrigin: string | null = null;
  try {
    directHubOrigin = getDirectHubOrigin();
  } catch {
    // otto-local:// without a stored hub is not an origin
  }
  return {
    pageOrigin: getPageHttpOrigin(),
    storedHubOrigin: getStoredHubOrigin(),
    directHubOrigin,
    apiUrl: config.apiUrl || '',
    cloudApiUrl: config.cloudApiUrl || '',
    mode: config.mode,
    nativeShell:
      isElectron() ||
      isCapacitor() ||
      config.mode === 'cloud' ||
      config.mode === 'electron' ||
      config.mode === 'capacitor',
  };
}

/**
 * Origin safe for DOM media elements (<audio>/<video>/<img>).
 * Always a real http(s) HUB origin — never otto-local, never file/null page origin,
 * and never the cloud app.
 */
export function getDomMediaOrigin(): string | null {
  return chooseDomMediaOrigin(currentMediaOriginCandidates());
}

/** True when progressive HTTP media playback against a local hub is possible. */
export function canPlayMediaLocally(): boolean {
  return getDomMediaOrigin() != null;
}

/**
 * @deprecated Prefer getDomMediaOrigin() for DOM media. Kept for debug dumps.
 * May return empty string when no local hub origin is available.
 */
export function getApiOrigin(): string {
  return getDomMediaOrigin() ?? '';
}

/** Warn once per un-fetchable hub URL, so a missing hub is not a silent blank. */
const warnedOnce = new Set<string>();
const WARN_CAP = 200;

/**
 * Resolve an image URL for DOM use.
 * - Relative paths (/s3/, /uploads/, /api/...) are absolutized against the hub
 *   DOM media origin, or replaced by the placeholder when there is no hub origin.
 * - Absolute http(s) /s3/ URLs are rewritten to the DOM origin. The presigned
 *   signature is validated by the proxy (nginx or the hub's /s3/ route) which
 *   pins the MinIO Host header, so the browser-facing host in the URL is
 *   irrelevant — this fixes e.g. `http://wasp.local:16161/s3/...` (a raw hub
 *   port that is often not reachable) being used from the Electron app or the
 *   nginx-served web, where the correct origin is https://…/s3/.
 */
export function resolveImageUrl(url?: string | null): string | null {
  const resolved = resolveMediaUrl(url, currentMediaOriginCandidates());
  if (isMediaPlaceholder(resolved) && url) {
    if (!warnedOnce.has(url) && warnedOnce.size < WARN_CAP) {
      warnedOnce.add(url);
      console.warn(
        '[mediaLibraryUrls] No hub origin reachable for this hub media URL — rendering a placeholder ' +
          'instead of asking the cloud origin, which cannot serve it. The bytes can still arrive ' +
          'through imageAssetsStore once the hub channel is up.',
        url,
      );
    }
  }
  return resolved;
}

/**
 * The hub URL that serves one `images` row's bytes: `/api/images/<id>/content`.
 *
 * This is the DOM fallback for a surface that has an image id but no blob yet
 * (the call overlay's avatar, card t_c41eef17). The route is `authenticateAny`,
 * which also accepts `?token=` — a raw `<img src>` cannot carry a Bearer header
 * — so the token travels in the query the way the media-library URLs already do
 * (`getMediaStreamUrl`). Verified against the dev hub: 401 without a credential,
 * `image/jpeg` with either form.
 *
 * The URL is put through `resolveMediaUrl`, so it is a HUB origin or nothing:
 * with no hub in reach the caller gets `null` and renders its own placeholder
 * instead of asking the cloud app (which answers this path with its HTML 404).
 */
export function imageContentUrlFor(
  imageId: unknown,
  token: string | null | undefined,
  candidates: MediaOriginCandidates,
): string | null {
  const id = String(imageId ?? '').trim();
  if (!id) return null;

  const resolved = resolveMediaUrl(`/api/images/${encodeURIComponent(id)}/content`, candidates);
  if (!resolved || isMediaPlaceholder(resolved)) return null;

  try {
    return withToken(new URL(resolved), token).toString();
  } catch {
    return null;
  }
}

/** `imageContentUrlFor` against the runtime this page is running in. */
export function getImageContentUrl(imageId: unknown, token?: string | null): string | null {
  return imageContentUrlFor(imageId, token, currentMediaOriginCandidates());
}

/**
 * The origin an ELECTRON announcement may be fetched from: the `otto-local://`
 * API base with its `/api` path removed, or null when this runtime has none.
 *
 * Electron's API base is `otto-local://<host>:<port>/api`, and the main process
 * proxies ANY path of that scheme to the hub it resolved
 * (`electron/main.ts` → `fetchLocalProtocolTarget` → `electron/localProtocolLadder.ts`,
 * `http://<host>:<port><path>`), including `/uploads/…`, with the self-signed-cert
 * handling the hub needs. `resolveMediaUrl` deliberately rejects that scheme, so
 * the DOM-media rule cannot express this case — hence the separate branch.
 */
function electronLocalMediaOrigin(apiUrl: string): string | null {
  if (!isElectronLocalApiUrl(apiUrl)) return null;
  const stripped = stripApiPath(apiUrl.trim());
  return stripped.startsWith(ELECTRON_LOCAL_PROTOCOL) ? stripped : null;
}

/**
 * Where an ANNOUNCEMENT's audio may be requested from — the `fetch()` analogue
 * of the DOM-media rule above, and a HUB origin or nothing.
 *
 * The hub's `userDevice:announce` payload carries a hub path (`/uploads/announce/<file>`
 * for a pre-recorded clip, `/uploads/tts/<file>` for a Piper one —
 * `TouchPanelService.announceToApps`). Two handlers consumed it by hand-building
 * `config.apiUrl.replace(/\/api$/, '') + path`, which in a cloud session is
 * `https://torik.ai/uploads/announce/…`: a hub path asked of the CLOUD app, which
 * answers its HTML 404 — the class t_1574d279 and t_c41eef17 closed for DOM
 * media, and card t_46b30a53 for this `fetch`. The bytes never arrived and the
 * announcement went silent.
 *
 * The DOM rule cannot simply be reused here:
 * - a `<audio>`/`<img>` src may be a placeholder; this is `fetch` + `decodeAudioData`,
 *   so a placeholder data URI would decode-fail into the handler's silent catch;
 * - `resolveMediaUrl` rejects `otto-local://`, which is exactly the origin Electron
 *   local mode works through today — routing this through it would silence a
 *   working Electron announcement.
 *
 * So: Electron keeps its `otto-local://` origin (byte-identical to the old
 * expression); everywhere else the hub origin the DOM decision picks (the
 * hub-served page origin, the stored LAN hub, or the configured hub API base)
 * serves the path; and with no hub in reach the result is null, meaning the
 * caller makes NO request at all rather than one the cloud app cannot answer.
 */
export function announceAudioUrlFor(audioUrl: unknown, candidates: MediaOriginCandidates): string | null {
  const raw = typeof audioUrl === 'string' ? audioUrl.trim() : '';
  if (!raw) return null;
  if (/^(data:|blob:)/i.test(raw)) return raw;

  if (/^https?:\/\//i.test(raw)) {
    // The hub is free to send an absolute URL (a storage host, a LAN address).
    // What it may never be is the cloud app: that origin answers hub paths with
    // its HTML 404, which is the request this module exists to prevent.
    try {
      return isCloudAppUrl(new URL(raw), candidates) ? null : raw;
    } catch {
      return null;
    }
  }

  if (!raw.startsWith('/')) return null;

  const electronOrigin = electronLocalMediaOrigin(candidates.apiUrl);
  if (electronOrigin) return electronOrigin + raw;

  const origin = chooseDomMediaOrigin(candidates);
  return origin ? origin + raw : null;
}

/** Warn once per un-fetchable announce path, so a missing hub is not a silent gap. */
const announceWarnedOnce = new Set<string>();

/**
 * `announceAudioUrlFor` against the runtime this page is running in. Returns the
 * URL to fetch the announcement's bytes from, or null — in which case the caller
 * must not request anything (the cloud origin cannot serve a hub path).
 */
export function resolveAnnounceAudioUrl(audioUrl: unknown): string | null {
  const resolved = announceAudioUrlFor(audioUrl, currentMediaOriginCandidates());
  if (!resolved && typeof audioUrl === 'string' && audioUrl.trim()) {
    if (!announceWarnedOnce.has(audioUrl) && announceWarnedOnce.size < WARN_CAP) {
      announceWarnedOnce.add(audioUrl);
      console.warn(
        '[mediaLibraryUrls] No hub origin reachable for this announcement audio — not asking the ' +
          'cloud origin, which cannot serve a hub path. The clip is unreachable until a hub is.',
        audioUrl,
      );
    }
  }
  return resolved;
}

export function getMediaUrlDebugInfo(): Record<string, unknown> {
  const config = getRuntimeConfig();
  return {
    mode: config.mode,
    apiUrl: config.apiUrl,
    wsUrl: config.wsUrl,
    storedHubOrigin: getStoredHubOrigin(),
    domMediaOrigin: getDomMediaOrigin(),
    canPlayMediaLocally: canPlayMediaLocally(),
    apiOrigin: getApiOrigin(),
    pageOrigin: typeof window !== 'undefined' ? window.location.origin : null,
  };
}

function withToken(url: URL, token?: string | null): URL {
  if (token) url.searchParams.set('token', token);
  return url;
}

function buildMediaUrl(
  itemId: string,
  suffix: 'stream' | 'thumbnail',
  token?: string | null,
  options: MediaUrlOptions = {},
): string | null {
  const origin = getDomMediaOrigin();
  if (!origin || !itemId) return null;

  try {
    const url = new URL(`/api/media-library/${itemId}/${suffix}`, origin);
    if (suffix === 'stream' && options.transcode) {
      url.searchParams.set('transcode', 'true');
    }
    return withToken(url, token).toString();
  } catch (err) {
    console.warn('[mediaLibraryUrls] Failed to build media URL', {
      itemId,
      suffix,
      origin,
      error: err,
    });
    return null;
  }
}

export function getMediaStreamUrl(itemId: string, token?: string | null, options: MediaUrlOptions = {}): string | null {
  return buildMediaUrl(itemId, 'stream', token, options);
}

export function getMediaThumbnailUrl(itemId: string, token?: string | null): string | null {
  return buildMediaUrl(itemId, 'thumbnail', token);
}
